import React, { useMemo } from 'react';
import { BeveledPanel } from '../Equipment/FabProcessTool/BeveledPanel';
import { getStockerMaterials } from './StockerMaterials';
import { shouldRenderAmhsPhysicalGlass } from '../rendering';
import type { AmhsDetailLevel } from '../rendering';
import type { StockerDimensions } from './stockerTypes';

export type StockerGlassProps = {
  dims: StockerDimensions;
  detail?: AmhsDetailLevel;
  opacity?: number;
};

/**
 * Left/right = cyan-blue glass, back (+Z) = dark black glass, front (−Z) open.
 */
export const StockerGlass: React.FC<StockerGlassProps> = ({
  dims,
  detail = 'high',
  opacity = 1,
}) => {
  const physical = shouldRenderAmhsPhysicalGlass(detail);
  const mats = useMemo(
    () => getStockerMaterials(opacity, physical),
    [opacity, physical]
  );
  /** Clear side sheets so rack FOUPs read through the enclosure. */
  const sideGlass = physical ? mats.sideGlass : mats.sideGlassDistant;
  const backGlass = physical ? mats.backBlackGlass : mats.backBlackGlassDistant;
  const topGlass = physical ? mats.topGrayGlass : mats.topGrayGlassDistant;
  const {
    width,
    depth,
    pillarBottom,
    pillarTop,
    capH,
    frameT,
  } = dims;

  const hx = width * 0.46;
  const hz = depth * 0.46;
  const panelD = Math.max(frameT * 0.32, depth * 0.028);
  const topThin = Math.max(frameT * 0.22, depth * 0.012, 0.022);
  const r = Math.min(width, depth) * 0.01;
  const sideGlassH = pillarTop - pillarBottom;
  const sideGlassY = pillarBottom + sideGlassH * 0.5;
  const sideGlassD = depth * 0.94;
  const topGlassY = pillarTop + capH * 0.12;

  return (
    <group name="StockerGlass">
      {/* Left / right — full-height cyan-blue transparent glass */}
      {([-1, 1] as const).map((side) => (
        <BeveledPanel
          key={`side-glass-${side}`}
          size={[panelD, sideGlassH, sideGlassD]}
          material={sideGlass}
          position={[side * (hx + side * panelD * 0.5), sideGlassY, 0]}
          radius={r * 0.2}
          smoothness={3}
        />
      ))}

      {/* Back (+Z) — dark black/smoked glass */}
      <BeveledPanel
        size={[width * 0.88, sideGlassH * 0.98, panelD]}
        material={backGlass}
        position={[0, sideGlassY, hz - panelD * 0.45]}
        radius={r * 0.35}
        smoothness={3}
      />

      {/* Top — gray color glass roof */}
      <BeveledPanel
        size={[width * 0.94, topThin, depth * 0.93]}
        material={topGlass}
        position={[0, topGlassY, 0]}
        radius={r * 0.15}
        smoothness={3}
      />
    </group>
  );
};

export default StockerGlass;
